/**
 * Shared builder for the Net Impact drill-in panel payload (PLT-4940).
 *
 * Four click surfaces on the company net-impact lens open the drill-in
 * panel: the impact bar and impact-category label (impact grain), and
 * the dimension bar and dimension label (dimension grain). They all
 * need to read the same four scores (positive/negative × cents-per-
 * dollar / dollars-per-year) off the `ScoreReader` and assemble an
 * identical payload — only the `target` differs. Factoring that here
 * keeps the four call sites a single `buildNetImpactDrillInPayload(...)`
 * line and guarantees they stay in sync (PLT-4915 originally inlined the
 * score reads in `impact-bars.tsx`; PLT-4940 lifts them out so the
 * dimension surfaces don't fork the logic).
 */
import { ModelEntityType, NamedModelEntityReference } from '@upright/common/model/input/types';
import type { ScoreReader } from '@upright/common/model/output/scores/score-reader';
import { FinalImpactScoreType } from '@upright/common/model/output/scores/types';
import { Valence } from '@upright/common/model/types';
import type { NetImpactLensData } from '../types';
import {
  CategorySideCentsPerDollar,
  drillInImpactCodes,
  NetImpactDrillInPayload,
  NetImpactDrillInTarget,
} from './state';

interface BuildArgs {
  /** Impact-category or dimension to drill into. */
  target: NetImpactDrillInTarget;
  /** Entity rendered on the originating page (company). */
  entity: { id: string; type: ModelEntityType; name: string | undefined };
  /** The lens score reader (from `NetImpactLensOptions.scoreReader`). */
  scoreReader: ScoreReader;
  /** Company revenue, needed to scale cents-per-dollar / dollars-per-year. */
  revenue: number | undefined;
  /** Model result id (`NetImpactLensData.modelResultId`, a number). */
  modelResultId: number | undefined;
  /** Product-name references from the lens data. */
  productNames: NamedModelEntityReference[] | undefined;
  /** Whether the viewer can open individual product pages. */
  hasProductViewAccess: boolean | undefined;
  /** Whether value-chain mode is on (selection-state flag ANDed with the
   *  `value-chain-splits` release gate, as computed by the chart). */
  showValueChains: boolean;
}

/** Read one valence's score for the target grain. The `ScoreReader`
 *  reads by impact code OR by dimension — never both — so we branch on
 *  the target and pass the matching argument. */
function readScoreForTarget(reader: ScoreReader, target: NetImpactDrillInTarget, valence: Valence): number | null {
  if (target.grain === 'impact') {
    return reader.readScore(target.impactCode, undefined, undefined, valence) ?? null;
  }
  return reader.readScore(undefined, target.dimension, undefined, valence) ?? null;
}

function readCategorySides(reader: ScoreReader, target: NetImpactDrillInTarget): CategorySideCentsPerDollar {
  const sides: CategorySideCentsPerDollar = {};
  for (const impactCode of drillInImpactCodes(target)) {
    sides[impactCode] = {
      [Valence.POSITIVE]: reader.readScore(impactCode, undefined, undefined, Valence.POSITIVE) ?? null,
      [Valence.NEGATIVE]: reader.readScore(impactCode, undefined, undefined, Valence.NEGATIVE) ?? null,
    };
  }
  return sides;
}

/**
 * Whether an impact-grain click (a bar or a category label) on this chart opens the drill-in
 * panel. Companies always do; a product only on the profile that opts in. The dimension surfaces
 * stay company-only: a product's panel has no per-product contributors to aggregate.
 */
export function opensImpactDrillIn(data: NetImpactLensData): boolean {
  const type = data.entity?.type;
  return type === ModelEntityType.COMPANY || (type === ModelEntityType.PRODUCT && data.opensDrillInPanel === true);
}

export function buildNetImpactDrillInPayload(args: BuildArgs): NetImpactDrillInPayload {
  const { target, entity, scoreReader, revenue, modelResultId, productNames, hasProductViewAccess, showValueChains } =
    args;

  // Pre-clone once per score type instead of once per read — `clone(...)`
  // allocates, and we read four scores (two valences × two units).
  const centsReader = scoreReader.clone({ scoreType: FinalImpactScoreType.CENTS_PER_DOLLAR, revenue });
  const dollarsReader = scoreReader.clone({ scoreType: FinalImpactScoreType.DOLLARS_PER_YEAR, revenue });

  const entityName = entity.name ?? 'this company';
  const pageHref =
    typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}${window.location.hash}` : '';

  return {
    target,
    positiveCentsPerDollar: readScoreForTarget(centsReader, target, Valence.POSITIVE),
    negativeCentsPerDollar: readScoreForTarget(centsReader, target, Valence.NEGATIVE),
    categorySideCentsPerDollar: readCategorySides(centsReader, target),
    positiveDollarsPerYear: readScoreForTarget(dollarsReader, target, Valence.POSITIVE),
    negativeDollarsPerYear: readScoreForTarget(dollarsReader, target, Valence.NEGATIVE),
    entity: { id: entity.id, type: entity.type, name: entity.name },
    // The `useCompanyDrilldownContributions` hook expects a string id;
    // `modelResultId` is typed as `number` on the lens data, so coerce.
    modelResultId: modelResultId !== undefined ? String(modelResultId) : undefined,
    productNames: productNames?.map(p => ({ id: p.id, name: p.name })),
    hasProductViewAccess: hasProductViewAccess ?? false,
    showValueChains,
    pageContext: {
      label: `${entityName} — Net impact`,
      href: pageHref,
      lensLabel: 'Net impact',
    },
  };
}
